import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { createLogger } from "../utils/logger";

const logger = createLogger("AppUrlListener");

/**
 * Handles deep links when the app is opened via custom URL scheme (panichefs://)
 * or Universal Links. Called after successful payment on iOS: Stripe redirects
 * to the web, which redirects to panichefs:// to open the app.
 */
const AppUrlListener = () => {
  const history = useHistory();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleUrl = (url) => {
      if (!url) return;
      logger.log("[DEEP LINK] Received URL:", url);

      try {
        // Handle panichefs:// scheme
        let path = "";
        let search = "";
        if (url.startsWith("panichefs://")) {
          const withoutScheme = url.replace("panichefs://", "");
          const [pathPart, searchPart] = withoutScheme.split("?");
          path = "/" + (pathPart || "").replace(/\/+/g, "/").replace(/^\//, "");
          search = searchPart ? `?${searchPart}` : "";
        } else if (url.startsWith("https://") || url.startsWith("http://")) {
          // Universal Link - extract path and query from full URL
          try {
            const parsed = new URL(url);
            path = parsed.pathname || "/";
            search = parsed.search || "";
          } catch (e) {
            logger.error("[DEEP LINK] Failed to parse URL:", e);
            return;
          }
        } else {
          return;
        }

        const fullPath = path + search;
        if (fullPath && fullPath !== "/") {
          logger.log("[DEEP LINK] Navigating to:", fullPath);
          history.replace(fullPath);
        }
      } catch (err) {
        logger.error("[DEEP LINK] Error handling URL:", err);
      }
    };

    // Handle URL when app is opened from background or cold start
    const listener = App.addListener("appUrlOpen", (event) => {
      handleUrl(event.url);
    });

    // Handle cold start - app launched via URL
    App.getLaunchUrl()
      .then((result) => {
        if (result?.url) {
          handleUrl(result.url);
        }
      })
      .catch(() => {});

    return () => {
      listener.then((l) => l.remove());
    };
  }, [history]);

  return null;
};

export default AppUrlListener;
